import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  createDefectGroup,
  deleteDefectGroup,
  fetchDefectGroups,
  updateDefectGroup,
} from "../../services/defectApi";
import { fetchCategories } from "../../services/deviceApi";
import TableScroll from "../../components/TableScroll";
import "../../styles/admin-common.css";

const DEFECT_CATS = ["SCREEN", "BATTERY", "BODY", "CAMERA", "PERFORMANCE", "ACCESSORIES", "OTHER"];
const SELECTION_TYPES = [
  { value: "SINGLE_CHOICE", label: "Pick one (single)" },
  { value: "MULTIPLE_CHOICE", label: "Select all that apply (multi)" },
];

const defaultSelectionForCategory = (category) =>
  ["SCREEN", "BATTERY", "CAMERA"].includes(category) ? "SINGLE_CHOICE" : "MULTIPLE_CHOICE";

const emptyGroup = {
  category: "SCREEN",
  name: "",
  description: "",
  selectionType: "SINGLE_CHOICE",
  displayOrder: "0",
};

const DefectGroups = () => {
  const [searchParams] = useSearchParams();
  const [categories, setCategories] = useState([]);
  const [groups, setGroups] = useState([]);
  const [productTypeId, setProductTypeId] = useState(searchParams.get("categoryId") || "");
  const [groupForm, setGroupForm] = useState(emptyGroup);
  const [editingGroupId, setEditingGroupId] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchCategories().then((cats) => {
      setCategories(cats);
      setProductTypeId((current) => {
        if (current) return current;
        if (cats.length === 0) return current;
        const mobile = cats.find((c) => c.name?.toLowerCase() === "mobile") || cats[0];
        return String(mobile.id);
      });
    }).catch(() => {});
  }, []);

  const loadGroups = async () => {
    if (!productTypeId) return;
    setLoading(true);
    setError("");
    try {
      const data = await fetchDefectGroups(productTypeId, { includeInactive: false });
      setGroups(data || []);
    } catch (err) {
      setError(err.message);
      setGroups([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadGroups();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productTypeId]);

  const resetGroupForm = () => {
    setGroupForm(emptyGroup);
    setEditingGroupId(null);
  };

  const handleSaveGroup = async (e) => {
    e.preventDefault();
    setError("");
    if (!groupForm.name.trim()) {
      setError("Group name is required");
      return;
    }
    const payload = {
      productTypeId: Number(productTypeId),
      category: groupForm.category,
      name: groupForm.name.trim(),
      description: groupForm.description.trim() || null,
      selectionType: groupForm.selectionType,
      displayOrder: parseInt(groupForm.displayOrder || "0", 10),
      isActive: true,
    };
    try {
      if (editingGroupId) {
        await updateDefectGroup(editingGroupId, payload);
      } else {
        await createDefectGroup(payload);
      }
      resetGroupForm();
      await loadGroups();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleEditGroup = (g) => {
    setEditingGroupId(g.id);
    setGroupForm({
      category: g.category,
      name: g.name || "",
      description: g.description || "",
      selectionType: g.selectionType || defaultSelectionForCategory(g.category),
      displayOrder: String(g.displayOrder ?? 0),
    });
  };

  const handleDeleteGroup = async (g) => {
    if (!g.canDelete) {
      setError(`Cannot delete — ${g.defectCount || 0} defect(s) still use this group.`);
      return;
    }
    if (!window.confirm(`Delete group "${g.name}"?`)) return;
    try {
      await deleteDefectGroup(g.id);
      await loadGroups();
    } catch (err) {
      setError(err.message);
    }
  };

  const sortedGroups = useMemo(
    () => [...groups].sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0)),
    [groups]
  );

  const productTypeName = categories.find((c) => String(c.id) === String(productTypeId))?.name || "—";

  return (
    <div className="admin-page">
      <h2>Defect groups</h2>
      <p className="subtitle">
        Group name + description + <strong>selection type</strong> +{" "}
        <strong>display order</strong> (controls app listing sequence —
        insert a new group between others by choosing an order number in between).
        Create defects on{" "}
        <Link to={`/defects?categoryId=${productTypeId || ""}`}>Central defects</Link>.
      </p>
      {error && <p className="error-msg">{error}</p>}

      <div className="admin-filters">
        <label className="field-label">
          Product type
          <select value={productTypeId} onChange={(e) => setProductTypeId(e.target.value)}>
            <option value="">Select type</option>
            {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </label>
      </div>

      {productTypeId && (
        <div className="admin-card">
          <h3>{editingGroupId ? "Edit group" : `Add group — ${productTypeName}`}</h3>
          <form className="admin-form admin-form-grid" onSubmit={handleSaveGroup}>
            <label className="field-label">
              Category
              <select
                value={groupForm.category}
                onChange={(e) => setGroupForm({
                  ...groupForm,
                  category: e.target.value,
                  selectionType: defaultSelectionForCategory(e.target.value),
                })}
              >
                {DEFECT_CATS.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </label>
            <label className="field-label">
              Selection type
              <select
                value={groupForm.selectionType}
                onChange={(e) => setGroupForm({ ...groupForm, selectionType: e.target.value })}
              >
                {SELECTION_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </label>
            <label className="field-label span-2">
              Group name
              <input
                value={groupForm.name}
                onChange={(e) => setGroupForm({ ...groupForm, name: e.target.value })}
                placeholder="e.g. Screen condition"
                required
              />
            </label>
            <label className="field-label span-2">
              Description
              <input
                value={groupForm.description}
                onChange={(e) => setGroupForm({ ...groupForm, description: e.target.value })}
                placeholder="e.g. Select screen defects that apply"
              />
            </label>
            <label className="field-label">
              Display order
              <input
                type="number"
                value={groupForm.displayOrder}
                onChange={(e) => setGroupForm({ ...groupForm, displayOrder: e.target.value })}
              />
            </label>
            <div className="form-actions">
              <button type="submit" className="admin-btn">
                {editingGroupId ? "Update group" : "Add group"}
              </button>
              {editingGroupId && (
                <button type="button" className="admin-btn secondary" onClick={resetGroupForm}>
                  Cancel
                </button>
              )}
            </div>
          </form>

          {loading ? <div className="admin-loading">Loading groups</div> : (
            <TableScroll>
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Order</th>
                    <th>Category</th>
                    <th>Group name</th>
                    <th>Description</th>
                    <th>Selection</th>
                    <th>Defects</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {sortedGroups.length === 0 && (
                    <tr><td colSpan={7} className="empty-msg">No groups yet</td></tr>
                  )}
                  {sortedGroups.map((g) => (
                    <tr key={g.id}>
                      <td>{g.displayOrder}</td>
                      <td>{g.category}</td>
                      <td>{g.name}</td>
                      <td>{g.description || "—"}</td>
                      <td>
                        {g.selectionType === "SINGLE_CHOICE" ? "Pick one" : "Multi"}
                      </td>
                      <td>{g.defectCount ?? 0}</td>
                      <td className="actions-cell">
                        <button type="button" className="admin-btn secondary" onClick={() => handleEditGroup(g)}>
                          Edit
                        </button>
                        {g.canDelete ? (
                          <button type="button" className="admin-btn danger" onClick={() => handleDeleteGroup(g)}>
                            Delete
                          </button>
                        ) : (
                          <small className="field-hint">Has defects</small>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </TableScroll>
          )}
        </div>
      )}
    </div>
  );
};

export default DefectGroups;
